import { useCallback, useContext, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChessUsernameContext } from "../../App";
import { useAuthToken } from "../../components/AuthWrapper";
import { useBackfillJob } from "../../hooks/useBackfillJob";
import type { UserProfile } from "../../types/user";
import { startGameSync } from "../../utils/apiConfig";
import { queryKeys } from "../../utils/queryKeys";
import { activeJobChanged, shouldStartSync, syncAskPlan, type SyncAsk } from "./gameSync";

/** POST /analyze-bulk {"mode":"sync"}, fire and forget: an error is silent, the next ask is a minute away. When the
 *  response names a job the cached profile doesn't hold, the profile is refetched so the banner and the "Analyzing…"
 *  rows find it. */
function useAskSync(): () => void {
  const { getAuthHeaders } = useAuthToken();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: () => startGameSync(getAuthHeaders),
    onSuccess: (result) => {
      const cached = queryClient.getQueryData<UserProfile>(queryKeys.userProfile);
      if (activeJobChanged(result, cached?.activeBackfillJobId)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.userProfile });
      }
    },
  });
  return useCallback(() => mutate(), [mutate]);
}

/**
 * Ask the server for the player's new games, app-wide (#336): mounted ONCE, in the app shell, so every signed-in,
 * linked page asks, on load, when the tab becomes visible or the window gets focus, every 60 s while the tab is
 * visible, and again for a newly linked username (#305). The rule is gameSync.syncAskPlan; this wires it to the
 * events and a timer. Nothing while hidden or while the user's job runs (the server would answer "running"). The
 * server still decides (one sync per 60 s, none while a job runs, the 50 newest since the newest stored game's day).
 * Takes the account from the shell, which provides ChessUsernameContext rather than reading it.
 */
export function useGameSync(account: { readonly chessUsername: string | null; readonly isLinked: boolean }): void {
  const { chessUsername, isLinked } = account;
  const { isSignedIn } = useAuthToken();
  const jobRunning = useBackfillJob().isRunning;
  const ask = useAskSync();
  const start = shouldStartSync({ isSignedIn, isLinked });
  // Survives StrictMode's second effect run, so a mount asks once.
  const last = useRef<SyncAsk | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = () => {
      clearTimeout(timer);
      const now = Date.now();
      const visible = document.visibilityState === "visible";
      const plan = syncAskPlan(last.current, { start, username: chessUsername, visible, jobRunning }, now);
      if (plan.kind === "ask") {
        last.current = { username: chessUsername, at: now };
        ask();
      }
      if (plan.kind !== "idle") timer = setTimeout(look, plan.inMs);
    };
    look();
    document.addEventListener("visibilitychange", look);
    window.addEventListener("focus", look);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", look);
      window.removeEventListener("focus", look);
    };
  }, [start, chessUsername, jobRunning, ask]);
}

/** The ask alone, for the home's refresh button (the shell's useGameSync does the asking otherwise). The server's
 *  limits still decide whether a sync starts. */
export function useGameSyncAsk(): () => void {
  const { isSignedIn } = useAuthToken();
  const { isLinked } = useContext(ChessUsernameContext);
  const ask = useAskSync();
  const start = shouldStartSync({ isSignedIn, isLinked });
  return useCallback(() => { if (start) ask(); }, [start, ask]);
}
